// EditFindingModal.jsx — edit an existing finding
import React, { useEffect, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import axios from "axios";
import "./AddFindingModal.css"; // reuse same styles

export default function EditFindingModal({
  show,
  onHide,
  row,
  onSaved,
  onToast,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    finding_id: "",
    previously_identified: "",
    severity: "",
    theme: "",
    finding: "",
    planned_remediation: "",
    owners: "",
    status: "",
    deadline: "",
    country: "",
    year: "",
    is_subsidiary: false,     // ✅ new
    subsidiary_name: "",      // ✅ new
  });

  useEffect(() => {
    if (row) {
      setForm({
        finding_id: row.finding_id || "",
        previously_identified: row.previously_identified || "",
        severity: row.severity || "",
        theme: row.theme || "",
        finding: row.finding || "",
        planned_remediation: row.planned_remediation || "",
        owners: row.owners || "",
        status: row.status || "",
        deadline: row.deadline || "",
        country: row.country || "",
        year: row.year || "",
        is_subsidiary: !!row.is_subsidiary,            // ✅
        subsidiary_name: row.subsidiary_name || "",    // ✅
      });
    }
  }, [row]);

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.finding_id) {
      onToast?.("Finding ID is required", "warning");
      return;
    }
    if (!form.severity || !form.finding) {
      onToast?.("Severity and Finding are required", "warning");
      return;
    }

    try {
      setSubmitting(true);
      const payload = { ...form, original_finding_id: row.finding_id };

      const res = await axios.put("/api/audit.php", payload, {
        withCredentials: true,
      });
      if (res.data?.success) {
        onSaved?.(res.data.data);
        onToast?.("Finding updated", "success");
        onHide?.();
      } else {
        onToast?.(res.data?.error || "Error updating finding", "danger");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err.message ||
        "Network error updating finding";
      onToast?.(msg, "danger");
    } finally {
      setSubmitting(false);
    }
  };

  if (!row) return null;

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : onHide}
      centered
      size="lg"
      backdrop="static"
      className="add-finding-modal"
    >
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>Edit Finding</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="row g-3">

            {/* Finding ID */}
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Finding ID</Form.Label>
                <Form.Control
                  type="text"
                  value={form.finding_id}
                  onChange={(e) =>
                    handleChange("finding_id", e.target.value.trim())
                  }
                  disabled={submitting}
                />
                <div className="form-text text-muted">Must be unique.</div>
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Previously Identified</Form.Label>
                <Form.Select
                  value={form.previously_identified}
                  onChange={(e) =>
                    handleChange("previously_identified", e.target.value)
                  }
                  disabled={submitting}
                >
                  <option value="">Select...</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Severity</Form.Label>
                <Form.Control
                  type="text"
                  value={form.severity}
                  onChange={(e) => handleChange("severity", e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Theme</Form.Label>
                <Form.Control
                  type="text"
                  value={form.theme}
                  onChange={(e) => handleChange("theme", e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Owner(s)</Form.Label>
                <Form.Control
                  type="text"
                  value={form.owners}
                  onChange={(e) => handleChange("owners", e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>
            </div>

            {/* ✅ Subsidiary fields */}
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Subsidiary?</Form.Label>
                <Form.Select
                  value={form.is_subsidiary ? "Yes" : "No"}
                  onChange={(e) =>
                    handleChange("is_subsidiary", e.target.value === "Yes")
                  }
                  disabled={submitting}
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </Form.Select>
              </Form.Group>
            </div>

            {form.is_subsidiary && (
              <div className="col-md-9">
                <Form.Group>
                  <Form.Label>Subsidiary Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter subsidiary name"
                    value={form.subsidiary_name}
                    onChange={(e) =>
                      handleChange("subsidiary_name", e.target.value)
                    }
                    disabled={submitting}
                  />
                </Form.Group>
              </div>
            )}
            {/* ✅ End Subsidiary fields */}

            <div className="col-12">
              <Form.Group>
                <Form.Label>Finding</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.finding}
                  onChange={(e) => handleChange("finding", e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>
            </div>

            <div className="col-12">
              <Form.Group>
                <Form.Label>Planned Remediation</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.planned_remediation}
                  onChange={(e) =>
                    handleChange("planned_remediation", e.target.value)
                  }
                  disabled={submitting}
                />
              </Form.Group>
            </div>

            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={form.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                  disabled={submitting}
                >
                  <option value="">Select...</option>
                  <option value="Open">Open</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Pending">Pending</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Deadline</Form.Label>
                <Form.Control
                  type="date"
                  value={form.deadline || ""}
                  onChange={(e) => handleChange("deadline", e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>
            </div>

            {/* Read-only context */}
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Country</Form.Label>
                <Form.Control type="text" value={form.country} disabled />
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Year</Form.Label>
                <Form.Control type="text" value={form.year} disabled />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn-profile btn btn-secondary"
            onClick={onHide}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-profile btn btn-success"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Saving…
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
